import "./ChatBoardArtifact.css";
import { Button } from "@ui/Button";
import { useLayoutAppKindOptional } from "@ui/layouts/layoutProvider";
import { ClipCount } from "@ui/molecules/ClipCount/ClipCount";
import { PrivatePublicIcon } from "@ui/molecules/PrivatePublicIcon/PrivatePublicIcon";
import { Pill } from "@ui/Pill";
import { HStack } from "@ui/Stack";
import { TextMono } from "@ui/typography/TextMono";
import { UpdatedAt } from "@ui/UpdatedAt";
import { pluralise } from "@utils/text";
import { createSignal, Show } from "solid-js";
import { type BoardItemListItemResponse, listBoardClips } from "@/client";
import {
	boardDetailsBasePath,
	boardDetailsRouteNeedsProject,
} from "~/modules/askgvt/boards/list/boardDetailsUrl";
import {
	BoardCard,
	type BoardCardThumbnail,
} from "~/modules/shared/boards/BoardCard/BoardCard";
import type { BoardArtifactClip } from "../ChatEventTypes.ts";

/** More than a chat carousel is worth scrolling through; the board page has the rest. */
const ALL_CLIPS_LIMIT = 100;

/** The board's details page in the current app, if it can be linked to. */
export const useBoardDetailsHref = (
	boardId: () => string | undefined,
	projectId?: () => string | undefined,
) => {
	const appKind = useLayoutAppKindOptional();

	return () => {
		const kind = appKind();
		const id = boardId();
		const project = projectId?.();
		if (!kind || !id) {
			return undefined;
		}
		if (boardDetailsRouteNeedsProject(kind) && !project) {
			return undefined;
		}
		return `${boardDetailsBasePath(kind, project)}/${id}`;
	};
};

type Props = {
	boardId?: string;
	href?: string;
	title?: string;
	clipsAdded: BoardArtifactClip[];
	/** The board as it is now, when it could be looked up. */
	privacy?: string;
	updatedAt?: string;
	clipCount?: number;
	deleted?: boolean;
};

/**
 * Headed like the boards list when the board's current details are known.
 * The event only carries the clips this answer added, so the rest of the board
 * is fetched the first time it is asked for.
 */
export const ChatBoardArtifact = (props: Props) => {
	const [showAll, setShowAll] = createSignal(false);
	const [boardClips, setBoardClips] =
		createSignal<BoardItemListItemResponse[]>();
	const [loadFailed, setLoadFailed] = createSignal(false);
	let loading = false;

	const hasMoreClips = () =>
		Boolean(props.boardId) &&
		!props.deleted &&
		(props.clipCount === undefined ||
			props.clipCount > props.clipsAdded.length);

	const loadBoardClips = async (boardId: string) => {
		loading = true;
		setLoadFailed(false);
		const response = await listBoardClips({
			path: { board_id: boardId },
			query: { page_size: ALL_CLIPS_LIMIT, sort: "created_at", asc: false },
		}).catch(() => undefined);
		loading = false;
		if (!response?.data) {
			setLoadFailed(true);
			setShowAll(false);
			return;
		}
		setBoardClips(response.data);
	};

	const toggleShowAll = () => {
		const next = !showAll();
		setShowAll(next);
		if (next && props.boardId && !boardClips() && !loading) {
			void loadBoardClips(props.boardId);
		}
	};

	const thumbnails = (): BoardCardThumbnail[] => {
		const all = showAll() ? boardClips() : undefined;
		if (!all) {
			return props.clipsAdded.map((clip) => ({
				url: clip.thumbnail_url ?? undefined,
				alt: clip.title,
			}));
		}
		const addedIds = new Set(props.clipsAdded.map((clip) => clip.clip_id));
		return all.map((clip) => ({
			url:
				clip.assets?.find((asset) => asset.type === "thumbnail")?.signed_url ??
				undefined,
			alt: clip.video?.title ?? undefined,
			highlighted: clip.id !== undefined && addedIds.has(clip.id),
		}));
	};

	const addedCount = () => props.clipsAdded.length;

	return (
		<>
			<BoardCard
				href={props.href}
				class={props.deleted ? "chat-board-artifact--deleted" : undefined}
				layout="carousel"
				title={props.title}
				titleSuffix={
					props.deleted ? (
						<Pill theme="grey600" variant="outline" size="small">
							Deleted
						</Pill>
					) : undefined
				}
				thumbnails={thumbnails()}
				info={
					<Show when={props.privacy}>
						{(privacy) => <PrivatePublicIcon privacy={privacy()} />}
					</Show>
				}
				meta={
					<Show when={props.updatedAt}>
						{(updatedAt) => (
							<>
								<UpdatedAt updatedAt={updatedAt()} />
								<ClipCount clipCount={props.clipCount ?? 0} />
							</>
						)}
					</Show>
				}
			/>
			<HStack
				class="chat-board-artifact-clips"
				justify="space-between"
				align="center"
			>
				<TextMono size="body" weight="bold" color="fg3">
					{addedCount()} {pluralise(addedCount(), "clip")} added
					<Show when={loadFailed()}> · couldn't load the rest</Show>
				</TextMono>
				<Show when={hasMoreClips()}>
					<Button
						monospaced
						variant="text"
						theme="monochrome"
						aria-pressed={showAll()}
						onClick={toggleShowAll}
					>
						{showAll() ? "show added" : "show all"}
					</Button>
				</Show>
			</HStack>
		</>
	);
};
